import { Link } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { siteMeta as staticMeta } from '../../data/navigation'
import { services } from '../../data/services'
import { contactInfo } from '../../data/contact'
import SocialIcons from '../ui/SocialIcons'

const API = import.meta.env.VITE_API_URL || '/api'

export default function Footer() {
  const [meta, setMeta] = useState(staticMeta)
  const currentYear = new Date().getFullYear()

  useEffect(() => {
    fetch(`${API}/navigation`).then(r => r.json()).then(d => {
      if (d) setMeta(prev => ({ ...prev, name: d.site_name || prev.name, tagline: d.site_tagline || prev.tagline, description: d.site_description || prev.description }))
    }).catch(() => {})
  }, [])

  return (
    <footer className="relative border-t border-white/5 bg-gray-950/50" role="contentinfo">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-premium-950/20 to-premium-950/40 pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-8">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-10">
          {/* Brand */}
          <div className="col-span-2 md:col-span-2 lg:col-span-2">
            <Link to="/" className="flex items-center gap-3 mb-6">
              <img
                src={meta.logo}
                alt={`${meta.name} logo`}
                className="w-14 h-14 object-contain"
                onError={(e) => {
                  ;(e.target as HTMLImageElement).style.display = 'none'
                  const parent = (e.target as HTMLImageElement).parentElement
                  if (parent) {
                    parent.innerHTML = `<div class="w-14 h-14 rounded-xl bg-gradient-to-br from-electric-blue to-electric-purple flex items-center justify-center text-white font-bold text-2xl">SST</div>`
                  }
                }}
              />
              <span className="text-xl font-bold text-white">
                {meta.name}
              </span>
            </Link>
            <p className="text-gray-400 text-sm leading-relaxed max-w-sm mb-6">
              {meta.description}
            </p>
            {/* Social links */}
            <SocialIcons />
          </div>

          {/* Services */}
          <div>
            <h3 className="text-white font-semibold text-sm uppercase tracking-wider mb-4">
              Services
            </h3>
            <ul className="space-y-3">
              {services.slice(0, 5).map((service) => (
                <li key={service.id}>
                  <Link
                    to="/services"
                    className="text-sm text-gray-400 hover:text-electric-blue transition-colors duration-200"
                  >
                    {service.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-white font-semibold text-sm uppercase tracking-wider mb-4">
              Quick Links
            </h3>
            <ul className="space-y-3">
              {[
                { label: 'Portfolio', path: '/portfolio' },
                { label: 'About Us', path: '/about' },
                { label: 'Blog', path: '/blog' },
                { label: 'Contact', path: '/contact' },
              ].map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-sm text-gray-400 hover:text-electric-blue transition-colors duration-200"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-white font-semibold text-sm uppercase tracking-wider mb-4">
              Contact
            </h3>
            <ul className="space-y-3 text-sm text-gray-400">
              <li>
                <a
                  href={`mailto:${contactInfo.email}`}
                  className="hover:text-electric-blue transition-colors duration-200"
                >
                  {contactInfo.email}
                </a>
              </li>
              <li>
                <a
                  href={`tel:${contactInfo.phone}`}
                  className="hover:text-electric-blue transition-colors duration-200"
                >
                  {contactInfo.phone}
                </a>
              </li>
              <li className="leading-relaxed">{contactInfo.address}</li>
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-16 pt-8 border-t border-white/5 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm text-gray-500">
            &copy; {currentYear} {meta.name}. All rights reserved.
          </p>
          <div className="flex gap-6 text-sm text-gray-500">
            <span className="hover:text-gray-400 cursor-pointer transition-colors">
              Privacy Policy
            </span>
            <span className="hover:text-gray-400 cursor-pointer transition-colors">
              Terms of Service
            </span>
          </div>
        </div>
      </div>
    </footer>
  )
}
